import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy } from "react";
import ProtectedRoute from "./ProtectedRoute";
import PublicOnlyRoute from "./PublicOnlyRoute";
import AuthLayout from "@/layouts/AuthLayout";
import MainLayout from "@/layouts/MainLayout";
import SimpleLayout from "@/layouts/SimpleLayout";

// Auth/error/public views — small enough to bundle eagerly.
import Login from "@/views/auth/Login";
import ForgotPassword from "@/views/auth/ForgotPassword";
import UpdatePassword from "@/views/auth/UpdatePassword";
import NotFound from "@/views/errors/NotFound";
import Presentation from "@/views/Presentation";

// Protected views — lazy-loaded so the login bundle stays small.
const Dashboard = lazy(() => import("@/views/Dashboard"));
const Machines = lazy(() => import("@/views/Machines"));
const MachineDetails = lazy(() => import("@/views/MachineDetails"));
const MachinesExport = lazy(() => import("@/views/MachinesExport"));
const Plants = lazy(() => import("@/views/Plants"));
const Alarms = lazy(() => import("@/views/Alarms"));
const Analytics = lazy(() => import("@/views/Analytics"));
const Maintenance = lazy(() => import("@/views/Maintenance"));
const Support = lazy(() => import("@/views/Support"));
const Documentation = lazy(() => import("@/views/Documentation"));
const Tutorials = lazy(() => import("@/views/Tutorials"));
const Integrations = lazy(() => import("@/views/Integrations"));
const Profile = lazy(() => import("@/views/Profile"));
const Settings = lazy(() => import("@/views/Settings"));
const PublicPanels = lazy(() => import("@/views/PublicPanels"));
const Alerts = lazy(() => import("@/views/Alerts"));
const Contacts = lazy(() => import("@/views/alerts/Contacts"));
const ContactGroups = lazy(() => import("@/views/alerts/ContactGroups"));
const DeviceAlerts = lazy(() => import("@/views/alerts/DeviceAlerts"));

export const router = createBrowserRouter([
  // Public presentation page (no layout)
  { path: "/presentation", element: <Presentation /> },

  // Legacy redirects from the Vue router
  { path: "/login", element: <Navigate to="/auth/login" replace /> },
  {
    path: "/forgot-password",
    element: <Navigate to="/auth/forgot-password" replace />,
  },

  // /auth/* — login/forgot-password. Already-authenticated users bounce to /.
  {
    path: "/auth",
    element: (
      <PublicOnlyRoute>
        <AuthLayout />
      </PublicOnlyRoute>
    ),
    children: [
      { path: "login", element: <Login /> },
      { path: "forgot-password", element: <ForgotPassword /> },
    ],
  },

  // /update-password — uses SimpleLayout (no branding), accepts ?key=...
  {
    path: "/update-password",
    element: <SimpleLayout />,
    children: [{ index: true, element: <UpdatePassword /> }],
  },

  // Protected app
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Dashboard /> },
      { path: "machines", element: <Machines /> },
      { path: "machines/export", element: <MachinesExport /> },
      { path: "machines/:id", element: <MachineDetails /> },
      { path: "public-panels", element: <PublicPanels /> },
      { path: "plants", element: <Plants /> },
      { path: "alarms", element: <Alarms /> },
      {
        path: "alerts",
        element: <Alerts />,
        children: [
          { index: true, element: <Navigate to="/alerts/contacts" replace /> },
          { path: "contacts", element: <Contacts /> },
          { path: "groups", element: <ContactGroups /> },
          { path: "device-alerts", element: <DeviceAlerts /> },
        ],
      },
      { path: "analytics", element: <Analytics /> },
      { path: "maintenance", element: <Maintenance /> },
      { path: "support", element: <Support /> },
      { path: "support/documentation", element: <Documentation /> },
      { path: "support/tutorials", element: <Tutorials /> },
      { path: "integrations", element: <Integrations /> },
      { path: "profile", element: <Profile /> },
      // Acessível a todo autenticado (como no Vue): as seções internas é que
      // são condicionadas por perfil (Empresa/Avançado só admin).
      { path: "settings", element: <Settings /> },
    ],
  },

  // 404
  { path: "*", element: <NotFound /> },
]);
